import { useState, useEffect } from 'react'
import { db } from '../firebase/config'

/**
 * De celsleutels die al in een gepubliceerd inzicht zitten.
 *
 * Een gepubliceerd inzicht bevriest zijn rijen; de celtypes die op ISS-cellen
 * leunen zetten daar de `cellId` als `key` neer. De verzameling hieruit zegt
 * dus welke cellen al buiten staan. (Celtypes die niet op ISS-cellen rekenen —
 * zoals de groeiranglijst per effect — leveren een andere soort sleutel; die
 * matcht nooit op een cellId, en dat klopt: zo'n inzicht dekt geen enkele cel.)
 *
 * `loaded` is een eigen toestand. Zolang die false is mag niemand beweren dat
 * een cel nog niet gepubliceerd is — dat is de verkeerde kant om te gokken:
 * je zou 'klaar om te publiceren' zetten bij iets dat al buiten staat.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 * AI-CONTEXT: See docs/features/intelligence-research-questions.md (§8)
 *
 * @returns {{keys: Set<string>, loaded: boolean, error: (Error|null)}}
 */
const usePublishedCellKeys = () => {
    const [keys, setKeys] = useState(/** @type {Set<string>} */(new Set()))
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState(/** @type {Error|null} */(null))

    useEffect(() => {
        setLoaded(false)
        setError(null)

        const unsub = db.collection('QuestionInsights')
            .where('Status', '==', 'published')
            .onSnapshot(
                (snap) => {
                    const gevonden = new Set()
                    snap.docs.forEach((doc) => {
                        const insight = doc.data()
                        // Zacht verwijderd = staat niet meer buiten, dus zijn
                        // cellen zijn weer vrij.
                        if (!insight || insight.Deleted === true) return
                        const rows = insight.Published && insight.Published.rows
                        if (!Array.isArray(rows)) return
                        rows.forEach((row) => { if (row && row.key) gevonden.add(row.key) })
                    })
                    setKeys(gevonden)
                    setLoaded(true)
                },
                (err) => {
                    console.error('usePublishedCellKeys:', err)
                    setError(err)
                    setKeys(new Set())
                    setLoaded(false)
                },
            )
        return () => unsub()
    }, [])

    return { keys, loaded, error }
}

export default usePublishedCellKeys
